import React, { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { MobileView, BrowserView } from "react-device-detect";
import { FieldGroup, InputCopyContent, InputGroup } from "../Input";
import Checkbox from "../Checkbox";
import Button from "../Button";
import TagsSelect from "../TagsSelect";
import axios from "axios";
import { bindActionCreators } from "redux";
import { useDispatch } from "react-redux";
import { actionCreators } from "../../state/index";
import Select from 'react-select';
import { API_ENDPOINTS } from "../../config/api";
import { useTheme } from "styled-components";
import ENV_CONFIG from "../../config/environment-unified";
import { toast } from "react-toastify";

const StartStreamV2 = ({ serverIP, streamKey, hideModal, eventSlug = null }) => {
  const [index, setIndex] = useState(0);
  const [streamConfig, setConfig] = useState({});

  return (
    <>
      {index === 1 ? (
        <SetupP
          setIndex={(e) => setIndex(e)}
          index={index}
          setConfig={(e) => {
            setConfig(e);
          }}
          streamConfig={streamConfig}
          serverIP={serverIP}
          streamKey={streamKey}
          hideModal={hideModal}
        />
      ) : (
        <GetStartedP
          setConfig={(e) => {
            setConfig(e);
          }}
          streamConfig={streamConfig}
          setIndex={(e) => setIndex(e)}
          index={index}
          eventSlug={eventSlug}
          hideModal={hideModal}
        />
      )}
    </>
  );
};

export default StartStreamV2;

const SetupP = ({ streamConfig, hideModal }) => {
  const [keys, setKeys] = useState(null);
  const [loadingKeys, setLoadingKeys] = useState(false);

  // Always refresh keys after starting stream so user sees the latest values
  // (and they also appear if the modal is reopened later).
  useEffect(() => {
    let mounted = true;
    setLoadingKeys(true);
    axios
      .get(API_ENDPOINTS.MY_STREAM_KEY, { withCredentials: true })
      .then((res) => {
        if (!mounted) return;
        setKeys(res.data || null);
      })
      .catch(() => {
        // non-blocking; fallback to whatever startstreaming returned
      })
      .finally(() => {
        if (!mounted) return;
        setLoadingKeys(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const effective = {
    ...(streamConfig || {}),
    ...(keys || {}),
  };
  const serverValue = effective.rtmpUrl || effective.injectionLink || "";
  const fullValue =
    effective.fullUrl ||
    (effective.injectionLink && effective.streamKey
      ? `${effective.injectionLink}${effective.streamKey}`
      : "");

  const handleSubmit = () => {
    hideModal();
  };

  return (
    <Setup>
      <h4>Live Stream Setup</h4>
      <Whattodo>
        <div>Copy and paste these settings into your streaming software or see</div>
        <a href="/how-to-stream">How to stream?</a>
      </Whattodo>
      <form autoComplete="off">
        <MobileView>
          <FieldGroup style={{ width: "95%" }} label="Server's IP and Stream Key">
            <InputCopyContent
              name="server-ip"
              placeholder="Server IP"
              value={fullValue}
              type="text"
              readOnly
            />
          </FieldGroup>
        </MobileView>
        <BrowserView>
          <FieldGroup style={{ width: "95%" }} label="Server's IP">
            <InputCopyContent
              name="server-ip"
              placeholder="Server IP"
              value={serverValue}
              type="text"
              readOnly
            />
          </FieldGroup>
          <FieldGroup style={{ width: "95%" }} label="Stream Key">
            <InputCopyContent
              name="stream-key"
              placeholder="Stream Key"
              value={effective?.streamKey || ""}
              type="text"
              readOnly
            />
          </FieldGroup>
        </BrowserView>

        <FieldGroup style={{ width: "95%" }} label="Playback (HLS)">
          <InputCopyContent
            name="playback-link"
            placeholder={loadingKeys ? "Loading..." : "Playback link"}
            value={effective?.playbackLink || ""}
            type="text"
            readOnly
          />
        </FieldGroup>

        <FieldGroup style={{ width: "95%" }} label="Full RTMP URL (optional)">
          <InputCopyContent
            name="full-rtmp-url"
            placeholder={loadingKeys ? "Loading..." : "Full RTMP URL"}
            value={fullValue}
            type="text"
            readOnly
          />
        </FieldGroup>

        <ButtonSection style={{ width: "95%" }}>
          <Button
            onClick={handleSubmit}
            style={{
              height: 32,
              width: "95%",
            }}
            variant="primary"
          >
            Done
          </Button>
        </ButtonSection>
      </form>
    </Setup>
  );
};

const GetStartedP = ({ setConfig, setIndex, hideModal, eventSlug }) => {
  const [tag, settag] = useState([]);
  const [gamesOptions, setGamesOptions] = useState([]);
  // Mandatory: every stream must be linked to an active event
  const [mode, setMode] = useState('event'); // keep state for minimal refactor
  const [eventData, setEventData] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [eventsOptions, setEventsOptions] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [useRandomGame, setUseRandomGame] = useState(true); // here: "no specific game / stream anything"
  const theme = useTheme();
  const didInitRestreamRef = useRef(false);
  const userTouchedRestreamRef = useRef(false);
  const [restreamCfg, setRestreamCfg] = useState({
    loading: false,
    loaded: false,
    enabled: false,
    twitchEnabled: false,
    twitchKey: "",
    twitchSaved: false,
    twitchMasked: "",
    youtubeEnabled: false,
    youtubeKey: "",
    youtubeSaved: false,
    youtubeMasked: "",
    facebookEnabled: false,
    facebookKey: "",
    facebookSaved: false,
    facebookMasked: "",
  });

  // Always fetch profile restream config so the Go Live checkbox reflects Settings → Multi‑streaming.
  useEffect(() => {
    let mounted = true;
    setRestreamCfg((prev) => ({ ...prev, loading: true }));
    axios
      .get("/api/stream/restream/config", { withCredentials: true })
      .then((res) => {
        if (!mounted) return;
        const cfg = res.data || {};
        setRestreamCfg((prev) => ({
          ...prev,
          loading: false,
          loaded: true,
          enabled: !!cfg.enabled,
          twitchEnabled: !!cfg.twitchEnabled,
          youtubeEnabled: !!cfg.youtubeEnabled,
          facebookEnabled: !!cfg.facebookEnabled,
          twitchSaved: !!(cfg.twitchKey && String(cfg.twitchKey).length > 0),
          youtubeSaved: !!(cfg.youtubeKey && String(cfg.youtubeKey).length > 0),
          facebookSaved: !!(cfg.facebookKey && String(cfg.facebookKey).length > 0),
          twitchMasked: cfg.twitchKey || "",
          youtubeMasked: cfg.youtubeKey || "",
          facebookMasked: cfg.facebookKey || "",
          // never prefill masked keys
          twitchKey: "",
          youtubeKey: "",
          facebookKey: "",
        }));
      })
      .catch(() => {
        if (!mounted) return;
        setRestreamCfg((prev) => ({ ...prev, loading: false, loaded: true }));
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch event data if eventSlug is provided
  useEffect(() => {
    if (eventSlug) {
      setLoadingEvent(true);
      axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${eventSlug}`, { withCredentials: true })
        .then(res => {
          const event = res.data.event;
          setEventData(event);

          // Pre-fill form with event data
          setdata({
            title: event.name || '',
            gameId: event.gameId || '',
            description: event.description || '',
            tags: event.tags || [],
            eventId: event.id,
            // enableRestream will be initialized once we also know the Profile setting (see effect below)
            enableRestream: false
          });
          // If event has a specific game, default to "Select a game" (not random).
          if (event.gameId) {
            setUseRandomGame(false);
          }

          // Set tags for TagsSelect component
          const tagsObjects = (event.tags || []).map(tag =>
            typeof tag === 'string' ? { label: tag, value: tag } : tag
          );
          settag(tagsObjects);
        })
        .catch(err => {
          console.error('Failed to load event:', err);
        })
        .finally(() => {
          setLoadingEvent(false);
        });
    }
  }, [eventSlug]);

  // Initialize Enable Restream once we know both Event defaults and Profile config.
  useEffect(() => {
    if (!restreamCfg.loaded) return;
    if (userTouchedRestreamRef.current) return;

    // Keep the Go Live checkbox synchronized with Profile (Settings → Multi‑streaming)
    const effectiveDefault = !!restreamCfg.enabled;
    didInitRestreamRef.current = true;

    setdata((prev) => ({ ...prev, enableRestream: effectiveDefault }));
  }, [restreamCfg.loaded, restreamCfg.enabled]);

  // Fetch games list
  useEffect(() => {
    axios
      .get(API_ENDPOINTS.GAMES)
      .then((res) => {
        const options = res.data.map((game) => ({
          value: game.id,
          label: game.name,
          icon: game.icon,
          shortName: game.shortName,
        }));
        // Pure game list; "no game" is handled by the checkbox, not as a fake game
        setGamesOptions(options);
      })
      .catch((err) => {
        console.error("❌ Error fetching games", err);
      });
  }, []);

  // Normalize event.gameId into an actual game UUID from the games list.
  // Some events store a short code (e.g. "CS") instead of the UUID.
  useEffect(() => {
    if (!eventData?.gameId || gamesOptions.length === 0) return;

    const raw = String(eventData.gameId);
    const direct = gamesOptions.find((g) => String(g.value) === raw);
    if (direct) {
      setUseRandomGame(false);
      setdata((prev) => (prev.gameId === direct.value ? prev : { ...prev, gameId: direct.value }));
      return;
    }

    const byShort = gamesOptions.find((g) => g.shortName && String(g.shortName) === raw);
    if (byShort) {
      setUseRandomGame(false);
      setdata((prev) => (prev.gameId === byShort.value ? prev : { ...prev, gameId: byShort.value }));
    }
  }, [eventData?.gameId, gamesOptions]);

  // Fetch events list when mode is 'event'
  useEffect(() => {
    if (mode === 'event' && !eventSlug) {
      setLoadingEvents(true);
      Promise.all([
        axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/my-events`, { withCredentials: true }),
        axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/my-invitations`, { withCredentials: true }),
        axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/joined-events`, { withCredentials: true })
      ])
        .then(([myEventsRes, invitationsRes, joinedRes]) => {
          const myEvents = myEventsRes.data?.events || [];
          const invitations = invitationsRes.data?.invitations || [];
          const joinedEvents = joinedRes.data?.events || [];

          const acceptedInvitedEvents = invitations
            .filter((inv) => inv?.status === 'active' && inv?.event)
            .map((inv) => inv.event);

          // Combine + de-dup by slug
          const bySlug = new Map();
          [...myEvents, ...acceptedInvitedEvents, ...joinedEvents].forEach((ev) => {
            if (ev?.slug && !bySlug.has(ev.slug)) bySlug.set(ev.slug, ev);
          });
          const events = Array.from(bySlug.values());

          // Only show eligible events in the "Stream to Event" picker.
          // - Tournament events: only after organizer started the tournament (tournamentActive=true)
          // - Non-tournament events: startDate <= now
          // - All events: exclude ended (endDate < now) and exclude cancelled/draft
          const now = new Date();
          const eligibleEvents = events.filter((event) => {
            const isTournament = event?.category?.slug === 'tournament' && !!event?.tournamentId;

            const status = String(event?.status || '').toLowerCase();
            const isLive = status === 'active';
            const hasEndedStatus = ['ended', 'finished', 'completed'].includes(status);
            const statusOk = status !== 'cancelled' && status !== 'draft' && !hasEndedStatus;

            const sd = event?.startDate ? new Date(event.startDate) : null;
            const startedByDate = sd && !Number.isNaN(sd.getTime()) && sd <= now;

            const ed = event?.endDate ? new Date(event.endDate) : null;
            const hasValidEnd = ed && !Number.isNaN(ed.getTime());
            const notEnded = !hasValidEnd || ed >= now;

            if (isTournament) {
              const started = isLive || ((typeof event?.tournamentActive === 'boolean')
                ? event.tournamentActive === true
                : startedByDate);
              return statusOk && started && notEnded;
            }
            return statusOk && (isLive || startedByDate) && notEnded;
          });

          const options = eligibleEvents.map((event) => ({
            value: event.slug,
            label: event.name,
            category: event.category?.name,
            startDate: event.startDate,
            endDate: event.endDate,
            timeZone: event.timeZone || event.timezone || null,
            tournamentActive: event.tournamentActive
          }));
          setEventsOptions(options);
          setLoadingEvents(false);
        })
        .catch((err) => {
          console.error("❌ Error fetching events for Go Live", err);
          setLoadingEvents(false);
        });
    }
  }, [mode, eventSlug]);

  const [errors, setErrors] = useState({
    title: "",
    game: "",
    tags: "",
    description: "",
  });

  const [data, setdata] = useState({
    title: "",
    gameId: "",
    tags: [],
    description: "",
    eventId: null,
    enableRestream: false
  });

  function gettags() {
    var tagss = [];
    for (let index = 0; index < tag.length; index++) {
      tagss.push(tag[index].label);
    }
    return tagss;
  }

  function handleErrors(e) {
    var error = { title: "", game: "", tags: "", description: "" };
    var ret = true;
    if (e.title === "") {
      error.title = "Title is required!";
      ret = false;
    }
    setErrors(error);
    return ret;
  }

  const dispatch = useDispatch();
  const { logout } = bindActionCreators(actionCreators, dispatch);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Must pick an eligible event when not coming from an event page
    if (!data?.eventId || String(data.eventId).trim() === "") {
      toast.error("Please select an active event to stream to.");
      return;
    }

    // IMPORTANT:
    // - Every stream MUST include eventId (backend enforces it).
    const startstreamdata = {
      title: data.title,
      // If user chose "no game", don't send any specific gameId; otherwise send selected gameId
      gameId: (mode === 'event' ? (data.gameId || undefined) : (useRandomGame ? undefined : data.gameId || undefined)),
      description: data.description,
      tags: gettags(),
      eventId: data.eventId,
      enableRestream: data.enableRestream
    };

    if (handleErrors(startstreamdata) === false) return;
    else {
      setErrors({ title: "", game: "", tags: "", description: "" });
    }

    (async () => {
      try {
        const res = await axios.post(API_ENDPOINTS.START_STREAM, startstreamdata, {
          withCredentials: true,
        });

        // Always refetch keys after starting so the next step shows correct values
        // even if startstreaming returns a partial payload.
        let k = null;
        try {
          const keyRes = await axios.get(API_ENDPOINTS.MY_STREAM_KEY, { withCredentials: true });
          k = keyRes.data || null;
        } catch (_) { }

        setConfig({ ...(res.data || {}), ...(k || {}) });
        setIndex(1);
      } catch (error) {
        if (error?.response?.status === 401) {
          logout();
          return;
        }
        toast.error(error?.response?.data?.message || "Failed to start stream");
        // eslint-disable-next-line no-console
        console.log(error);
      }
    })();
  };

  const handleChange = (e) => {
    setdata({ ...data, [e.target.name]: e.target.value });
  };

  // Load restream config on-demand when user enables it
  useEffect(() => {
    let mounted = true;
    if (!data.enableRestream) return;

    setRestreamCfg((prev) => ({ ...prev, loading: true }));
    axios
      .get("/api/stream/restream/config", { withCredentials: true })
      .then((res) => {
        const cfg = res.data || {};
        if (!mounted) return;
        setRestreamCfg((prev) => ({
          ...prev,
          loading: false,
          loaded: true,
          enabled: !!cfg.enabled,
          twitchEnabled: !!cfg.twitchEnabled,
          youtubeEnabled: !!cfg.youtubeEnabled,
          facebookEnabled: !!cfg.facebookEnabled,
          twitchSaved: !!(cfg.twitchKey && String(cfg.twitchKey).length > 0),
          youtubeSaved: !!(cfg.youtubeKey && String(cfg.youtubeKey).length > 0),
          facebookSaved: !!(cfg.facebookKey && String(cfg.facebookKey).length > 0),
          twitchMasked: cfg.twitchKey || "",
          youtubeMasked: cfg.youtubeKey || "",
          facebookMasked: cfg.facebookKey || "",
          // never prefill masked keys
          twitchKey: "",
          youtubeKey: "",
          facebookKey: "",
        }));
      })
      .catch(() => {
        if (!mounted) return;
        setRestreamCfg((prev) => ({ ...prev, loading: false }));
      });

    return () => {
      mounted = false;
    };
  }, [data.enableRestream]);

  const persistProfileRestreamEnabled = async (enabled) => {
    try {
      let cfg = restreamCfg;
      if (!cfg.loaded) {
        const res = await axios.get("/api/stream/restream/config", { withCredentials: true });
        const fresh = res.data || {};
        cfg = {
          ...cfg,
          loaded: true,
          enabled: !!fresh.enabled,
          twitchEnabled: !!fresh.twitchEnabled,
          youtubeEnabled: !!fresh.youtubeEnabled,
          facebookEnabled: !!fresh.facebookEnabled
        };
      }

      // Persist only the enabled flag + platform activation toggles (NO KEYS from Go Live popup)
      await axios.put(
        "/api/stream/restream/config",
        {
          enabled: !!enabled,
          twitchEnabled: !!cfg.twitchEnabled,
          youtubeEnabled: !!cfg.youtubeEnabled,
          facebookEnabled: !!cfg.facebookEnabled
        },
        { withCredentials: true }
      );

      setRestreamCfg((prev) => ({
        ...prev,
        loaded: true,
        enabled: !!enabled,
        twitchEnabled: !!cfg.twitchEnabled,
        youtubeEnabled: !!cfg.youtubeEnabled,
        facebookEnabled: !!cfg.facebookEnabled
      }));
    } catch (e) {
      toast.error("Failed to update Restream setting. Please check Settings → Multi‑streaming.");
    }
  };

  const saveRestreamSettings = async () => {
    try {
      setRestreamCfg((p) => ({ ...p, loading: true }));
      const payload = {
        // Only persist platform activation toggles from this popup (no keys)
        // Keep current profile "enabled" value unchanged.
        enabled: !!restreamCfg.enabled,
        twitchEnabled: !!restreamCfg.twitchEnabled,
        youtubeEnabled: !!restreamCfg.youtubeEnabled,
        facebookEnabled: !!restreamCfg.facebookEnabled,
      };

      await axios.put("/api/stream/restream/config", payload, { withCredentials: true });

      // Refresh to reflect updated toggles (keys are managed elsewhere)
      const res = await axios.get("/api/stream/restream/config", { withCredentials: true });
      const cfg = res.data || {};
      setRestreamCfg((prev) => ({
        ...prev,
        loading: false,
        loaded: true,
        enabled: !!cfg.enabled,
        twitchEnabled: !!cfg.twitchEnabled,
        youtubeEnabled: !!cfg.youtubeEnabled,
        facebookEnabled: !!cfg.facebookEnabled,
        twitchSaved: !!(cfg.twitchKey && String(cfg.twitchKey).length > 0),
        youtubeSaved: !!(cfg.youtubeKey && String(cfg.youtubeKey).length > 0),
        facebookSaved: !!(cfg.facebookKey && String(cfg.facebookKey).length > 0),
        twitchMasked: cfg.twitchKey || "",
        youtubeMasked: cfg.youtubeKey || "",
        facebookMasked: cfg.facebookKey || "",
        // never prefill keys here
        twitchKey: "",
        youtubeKey: "",
        facebookKey: "",
      }));
      toast.success("Platform activations saved");
    } catch (e) {
      setRestreamCfg((p) => ({ ...p, loading: false }));
      toast.error("Failed to save restream settings");
    }
  };

  if (loadingEvent) {
    return (
      <Setup>
        <h4>Loading Event...</h4>
        <p style={{ color: '#888', textAlign: 'center' }}>Please wait...</p>
      </Setup>
    );
  }

  return (
    <Setup>
      <h4>🎥 Go Live - Stream to Event</h4>

      {/* Mode Selection - Only show if NOT coming from an event */}
      {!eventSlug && (
        <ModeSelection>
          <ModeCard
            selected={mode === 'event'}
            onClick={() => setMode('event')}
          >
            <ModeIcon>🎪</ModeIcon>
            <ModeTitle>Stream to Event</ModeTitle>
            <ModeDescription>Link your stream to an existing event</ModeDescription>
          </ModeCard>
        </ModeSelection>
      )}

      {/* Event Selection - Show if mode is 'event' and no eventSlug provided */}
      {mode === 'event' && !eventSlug && (
        <FieldGroup style={{ width: "95%", marginBottom: '1.5rem' }} label="Select Event">
          <Select
            id="selectEvent"
            options={eventsOptions}
            onChange={(selectedOption) => {
              if (selectedOption) {
                // Fetch the selected event data
                setLoadingEvent(true);
                axios
                  .get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${selectedOption.value}`, { withCredentials: true })
                  .then((res) => {
                    const event = res.data.event;
                    if (!event) {
                      console.error("❌ Error: event payload missing in /events/:slug response");
                      setLoadingEvent(false);
                      return;
                    }
                    setEventData(event);
                    setdata({
                      ...data,
                      title: event.name || '',
                      gameId: event.gameId || '',
                      tags: event.tags || [],
                      eventId: event.id,
                      // enableRestream will be initialized once we also know the Profile setting (see effect above)
                      enableRestream: false
                    });
                    // If the event has a specific game, switch off "Random" automatically.
                    if (event.gameId) {
                      setUseRandomGame(false);
                    }
                    // Sync local tags state for TagsSelect
                    const tagsObjects = (event.tags || []).map(tag =>
                      typeof tag === 'string' ? { label: tag, value: tag } : tag
                    );
                    settag(tagsObjects);
                    setLoadingEvent(false);
                  })
                  .catch((err) => {
                    console.error("❌ Error fetching event", err);
                    setLoadingEvent(false);
                  });
              } else {
                setEventData(null);
                setdata({
                  title: "",
                  gameId: "",
                  tags: [],
                  description: "",
                  eventId: null,
                  enableRestream: false
                });
              }
            }}
            placeholder={loadingEvents ? "Loading events..." : "Select an event to stream to"}
            isClearable
            isLoading={loadingEvents}
            noOptionsMessage={() => "No eligible events. For tournaments, the organizer must click “Start Tournament” (or update event-service to return tournamentActive)."}
            formatOptionLabel={(option) => (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 'bold' }}>{option.label}</span>
                <span style={{ fontSize: '0.85em', color: '#888' }}>
                  {(() => {
                    const tz = option.timeZone || undefined;
                    const fmt = (d) => {
                      if (!d) return '';
                      const dd = new Date(d);
                      if (Number.isNaN(dd.getTime())) return '';
                      return dd.toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        ...(tz ? { timeZone: tz } : {}),
                      });
                    };
                    const start = fmt(option.startDate);
                    const end = fmt(option.endDate);
                    const range = end ? `${start} → ${end}` : start;
                    return `${option.category || ''}${range ? ` • ${range}` : ''}`;
                  })()}
                </span>
              </div>
            )}
            styles={{
              control: (base, state) => ({
                ...base,
                backgroundColor: theme.color.background,
                borderColor: state.isFocused ? theme.color.primary : theme.color.border,
                boxShadow: 'none',
                color: theme.color.primaryText,
                minHeight: 38
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: theme.color.background,
                border: `1px solid ${theme.color.border}`,
                zIndex: 9999
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused ? theme.color.primary : theme.color.background,
                color: state.isFocused ? '#fff' : theme.color.primaryText,
                cursor: 'pointer',
                padding: '10px'
              }),
              singleValue: (base) => ({
                ...base,
                color: theme.color.primaryText
              }),
              placeholder: (base) => ({
                ...base,
                color: theme.color.secondaryText
              }),
              input: (base) => ({
                ...base,
                color: theme.color.primaryText
              })
            }}
          />
          {loadingEvent && <HelpText>Loading event details...</HelpText>}
        </FieldGroup>
      )}

      {/* Event Info Banner - Show if streaming to event */}
      {mode === 'event' && eventData && (
        <EventBanner>
          <EventIcon>🎪</EventIcon>
          <EventInfo>
            <EventName>{eventData.name}</EventName>
            <EventMeta>
              {(() => {
                const tz = eventData?.timeZone || eventData?.timezone || undefined;
                const sd = eventData?.startDate ? new Date(eventData.startDate) : null;
                const start = (sd && !Number.isNaN(sd.getTime()))
                  ? sd.toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    ...(tz ? { timeZone: tz } : {}),
                  })
                  : '';
                return `${eventData.category?.name || ''}${start ? ` • ${start}` : ''}`;
              })()}
            </EventMeta>
          </EventInfo>
        </EventBanner>
      )}

      <form autoComplete="off" onSubmit={handleSubmit}>
        <FieldGroup style={{ width: "95%" }} label="Stream Title">
          <InputGroup
            value={data.title}
            name="title"
            placeholder="Enter stream title"
            type="text"
            onChange={handleChange}
            alert={errors.title}
            disabled={mode === 'event' && eventData}
          />
          {mode === 'event' && eventData && (
            <HelpText>Using event title</HelpText>
          )}
        </FieldGroup>

        <FieldGroup style={{ width: "95%" }} label="Game">
          {/* When streaming to an event, game must come from the event (read-only). */}
          {mode === 'event' ? (
            <>
              <Select
                id="startStreamGame"
                options={gamesOptions}
                isDisabled
                placeholder={eventData?.gameId ? "Loading game..." : "No game set on this event"}
                value={gamesOptions.find(opt => opt.value === data.gameId) || null}
                formatOptionLabel={(option) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {option.icon && (
                      <img src={option.icon} alt={option.label} style={{ width: 20, height: 20, borderRadius: 4 }} />
                    )}
                    <span>{option.label}</span>
                  </div>
                )}
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: theme.color.background,
                    borderColor: theme.color.border,
                    boxShadow: 'none',
                    color: theme.color.primaryText,
                    minHeight: 38,
                    opacity: 0.8,
                    cursor: 'not-allowed'
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: theme.color.secondaryBg,
                    color: theme.color.primaryText,
                    zIndex: 10
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: theme.color.primaryText
                  }),
                  input: (base) => ({
                    ...base,
                    color: theme.color.primaryText
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: theme.color.secondaryIcon
                  })
                }}
              />
              <HelpText>Using the game configured on the event.</HelpText>
            </>
          ) : (
            <>
              {/* Toggle between "no game" and "specific game" */}
              <div style={{ width: "100%", marginBottom: '8px' }}>
                {/* IMPORTANT: Checkbox already renders a <label>. Do not wrap it in another <label> (invalid HTML, can cause toggling). */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <Checkbox
                    name="useNoGame"
                    checked={useRandomGame}
                    onChange={() => {
                      setUseRandomGame(true);
                      // No specific game selected when this is on
                      setdata({ ...data, gameId: '' });
                    }}
                  >
                    🎲 Random
                  </Checkbox>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    name="useSpecificGame"
                    checked={!useRandomGame}
                    onChange={() => {
                      setUseRandomGame(false);
                      setdata({ ...data, gameId: '' });
                    }}
                  >
                    🎮 Select a specific game
                  </Checkbox>
                </div>
              </div>

              {/* Show game list only when user chose "Select a specific game" */}
              {!useRandomGame && (
                <Select
                  id="startStreamGame"
                  options={gamesOptions}
                  onChange={(selectedOption) => {
                    // If user interacts with game selection, we must stay in "specific game" mode.
                    setUseRandomGame(false);
                    setdata({ ...data, gameId: selectedOption?.value || '' });
                  }}
                  placeholder="Select a game"
                  isClearable
                  value={gamesOptions.find(opt => opt.value === data.gameId) || null}
                  formatOptionLabel={(option) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {option.icon && (
                        <img src={option.icon} alt={option.label} style={{ width: 20, height: 20, borderRadius: 4 }} />
                      )}
                      <span>{option.label}</span>
                    </div>
                  )}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      backgroundColor: theme.color.background,
                      borderColor: state.isFocused ? theme.color.primary : theme.color.border,
                      boxShadow: 'none',
                      color: theme.color.primaryText,
                      minHeight: 38
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: theme.color.secondaryBg,
                      color: theme.color.primaryText,
                      zIndex: 10
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected
                        ? theme.color.primary
                        : state.isFocused
                          ? theme.color.secondaryBg
                          : 'transparent',
                      color: state.isSelected ? theme.color.background : theme.color.primaryText,
                      cursor: 'pointer'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: theme.color.primaryText
                    }),
                    input: (base) => ({
                      ...base,
                      color: theme.color.primaryText
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: theme.color.secondaryIcon
                    })
                  }}
                />
              )}
              {useRandomGame && (
                <HelpText>🎲 Random: no specific game selected, you can stream anything.</HelpText>
              )}
              {errors.game && <Span alert={errors.game}>{errors.game}</Span>}
            </>
          )}
        </FieldGroup>

        <FieldGroup style={{ width: "95%" }} label="Tags">
          <TagsSelect
            maxTags={5}
            tags={tag}
            settags={(e) => {
              settag(e);
            }}
            setdata={(e) => {
              setdata({ ...data, tags: e });
            }}
            placeholder="Add tags (optional)"
          />
          {errors.tags && <Span alert={errors.tags}>{errors.tags}</Span>}
        </FieldGroup>

        <FieldGroup style={{ width: "95%" }} label="Description (Optional)">
          <TextArea
            value={data.description}
            name="description"
            placeholder="Describe your stream..."
            onChange={handleChange}
            rows={3}
          />
        </FieldGroup>

        {/* Restream Option */}
        <label
          style={{
            width: "95%",
            display: "flex",
            marginBottom: "16px",
            marginTop: "10px",
            alignItems: "flex-start"
          }}
        >
          <Checkbox
            name="enableRestream"
            checked={data.enableRestream}
            onChange={(e) => {
              const checked = !!e.target.checked;
              userTouchedRestreamRef.current = true;
              setdata((prev) => ({ ...prev, enableRestream: checked }));
              setRestreamCfg((prev) => ({ ...prev, enabled: checked }));
              // Sync with Profile (enabled flag only; no key updates)
              persistProfileRestreamEnabled(checked);
            }}
          />
          <div style={{ marginLeft: '8px' }}>
            <span style={{ fontWeight: 600 }}>📡 Enable Restream</span>
            <HelpText style={{ marginTop: '4px' }}>
              Simultaneously stream to your configured platforms (Twitch, YouTube, Facebook)
            </HelpText>
          </div>
        </label>

        {/* Platform activations only (no key update from popup) */}
        {data.enableRestream && (
          <div style={{ width: "95%", marginBottom: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: theme.color.primaryText }}>
              🔁 Restream Platforms
            </div>
            <HelpText>
              Toggle which platforms to restream to. Platform keys are managed in Settings → Multi‑streaming.
            </HelpText>

            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              <div>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Checkbox
                    name="twitchEnabled"
                    checked={restreamCfg.twitchEnabled}
                    onChange={(e) => setRestreamCfg((p) => ({ ...p, twitchEnabled: e.target.checked }))}
                  />
                  <span style={{ fontWeight: 600 }}>Twitch</span>
                </label>
              </div>

              <div>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Checkbox
                    name="youtubeEnabled"
                    checked={restreamCfg.youtubeEnabled}
                    onChange={(e) => setRestreamCfg((p) => ({ ...p, youtubeEnabled: e.target.checked }))}
                  />
                  <span style={{ fontWeight: 600 }}>YouTube</span>
                </label>
              </div>

              <div>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Checkbox
                    name="facebookEnabled"
                    checked={restreamCfg.facebookEnabled}
                    onChange={(e) => setRestreamCfg((p) => ({ ...p, facebookEnabled: e.target.checked }))}
                  />
                  <span style={{ fontWeight: 600 }}>Facebook</span>
                </label>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", width: "100%", marginTop: 12 }}>
              <Button
                variant="primary"
                type="button"
                onClick={saveRestreamSettings}
                disabled={restreamCfg.loading}
                style={{ height: 36 }}
              >
                {restreamCfg.loading ? "Saving..." : "Save Platforms"}
              </Button>
            </div>
          </div>
        )}

        <Button
          style={{
            height: "40px",
            width: "95%",
            marginTop: "15px",
            fontSize: "16px",
            fontWeight: 600
          }}
          variant="primary"
          type="submit"
          disabled={!data?.eventId || String(data.eventId).trim() === ""}
        >
          🎪 Go Live to Event
        </Button>
      </form>
    </Setup>
  );
};

// Styled Components
const StartStreamtyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  h4 {
    color: ${({ theme }) => theme.color.primaryText};
    margin-bottom: 25px;
    width: 100%;
    text-align: center;
  }
  form {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }
`;

const Setup = styled.div`
  ${StartStreamtyle}
`;

const ModeSelection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  width: 95%;
  margin-bottom: 20px;
`;

const ModeCard = styled.div`
  background: ${props => props.selected ? '#2a2a2a' : '#1a1a1a'};
  border: 2px solid ${props => props.selected ? props.theme.color.primary : '#333'};
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  
  &:hover {
    border-color: ${props => props.theme.color.primary};
    background: #2a2a2a;
  }
`;

const ModeIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
`;

const ModeTitle = styled.div`
  color: ${({ theme }) => theme.color.primaryText};
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 5px;
`;

const ModeDescription = styled.div`
  color: #888;
  font-size: 11px;
  line-height: 1.4;
`;

const EventBanner = styled.div`
  width: 95%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const EventIcon = styled.div`
  font-size: 32px;
`;

const EventInfo = styled.div`
  flex: 1;
`;

const EventName = styled.div`
  color: white;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 4px;
`;

const EventMeta = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
`;

const HelpText = styled.div`
  color: #888;
  font-size: 11px;
  margin-top: 5px;
  line-height: 1.4;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  background: ${({ theme }) => theme.color.background};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.color.primaryText};
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.primary};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.color.secondaryIcon};
  }
`;

const Whattodo = styled.div`
  width: 100%;
  margin-bottom: 15px;
  text-align: center;
  div {
    width: 100%;
    color: #ababab;
    font-family: "Poppins Medium";
    font-style: normal;
    font-weight: 500;
    font-size: 11px;
    line-height: 120%;
  }
  a {
    color: ${({ theme }) => theme.color.primaryText};
    font-family: "Poppins Medium";
    font-style: normal;
    font-weight: 500;
    font-size: 11px;
    line-height: 120%;
  }
`;

const ButtonSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const Span = styled.span`
  display: block;
  margin-top: 5px;
  font-size: 12px;
  letter-spacing: 0.4px;
  font-family: "Poppins Regular";
  ${({ alert }) =>
    alert &&
    `
    display: block;
    color: rgb(238, 76, 80);
  `}
`;

